/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from antd/components/input/input.tsx
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import classNames from 'classnames';
import omit from 'rc-util/lib/omit';
import ClearableLabeledInput from './ClearableLabeledInput';
import { ConfigConsumer } from '../config-provider';
import SizeContext from '../config-provider/SizeContext';
import warning from 'antd/es/_util/warning';
import { hasPrefixSuffix, getInputClassName } from './utils';
import { Loading } from '../loading';
import { Icon } from '../icon';
// B-Design
export const getInputClassNames = (props, breathing, classPrefix, className) => {
    const { state, className: propsClassName, size, disabled, theme } = props;
    return classNames({
        [classPrefix + '-xs']: size === 'xs',
        [classPrefix + '-error']: state === 'error',
        [classPrefix + '-warning']: state === 'warning',
        [classPrefix + '-disabled']: disabled === true,
        [classPrefix + '-gray']: theme === 'gray',
        [classPrefix + '-breathing']: breathing
    }, className ? className : propsClassName);
};
// End
export function fixControlledValue(value) {
    if (typeof value === 'undefined' || value === null) {
        return '';
    }
    return String(value);
}
export function resolveOnChange(target, e, onChange, targetValue) {
    if (!onChange) {
        return;
    }
    let event = e;
    if (e.type === 'click') {
        // Clone a new target for event.
        // Avoid the following usage, the setQuery method gets the original value.
        //
        // const [query, setQuery] = React.useState('');
        // <Input
        //   allowClear
        //   value={query}
        //   onChange={(e)=> {
        //     setQuery((prevStatus) => e.target.value);
        //   }}
        // />
        const currentTarget = target.cloneNode(true);
        // click clear icon
        event = Object.create(e, {
            target: { value: currentTarget },
            currentTarget: { value: currentTarget }
        });
        currentTarget.value = '';
        onChange(event);
        return;
    }
    // Trigger by composition event, this means we need force change the input value
    if (targetValue !== undefined) {
        event = Object.create(e, {
            target: { value: target },
            currentTarget: { value: target }
        });
        target.value = targetValue;
        onChange(event);
        return;
    }
    onChange(event);
}
export function triggerFocus(element, option) {
    if (!element)
        return;
    element.focus(option);
    // Selection content
    const { cursor } = option || {};
    if (cursor) {
        const len = element.value.length;
        switch (cursor) {
            case 'start':
                element.setSelectionRange(0, 0);
                break;
            case 'end':
                element.setSelectionRange(len, len);
                break;
            default:
                element.setSelectionRange(0, len);
        }
    }
}
class Input extends React.Component {
    static Group;
    static Search;
    static TextArea;
    static Password;
    static defaultProps = {
        type: 'text'
    };
    input;
    clearableInput;
    removePasswordTimeout;
    direction = 'ltr';
    constructor(props) {
        super(props);
        const value = typeof props.value === 'undefined' ? props.defaultValue : props.value;
        this.state = {
            value,
            focused: false,
            // eslint-disable-next-line react/no-unused-state
            prevValue: props.value
        };
    }
    static getDerivedStateFromProps(nextProps, { prevValue }) {
        const newState = { prevValue: nextProps.value };
        if (nextProps.value !== undefined || prevValue !== nextProps.value) {
            newState.value = nextProps.value;
        }
        if (nextProps.disabled) {
            newState.focused = false;
        }
        return newState;
    }
    componentDidMount() {
        this.clearPasswordValueAttribute();
    }
    // Since polyfill `getSnapshotBeforeUpdate` need work with `componentDidUpdate`.
    // We keep an empty function here.
    componentDidUpdate() { }
    getSnapshotBeforeUpdate(prevProps) {
        if (hasPrefixSuffix(prevProps) !== hasPrefixSuffix(this.props)) {
            if (process.env.NODE_ENV !== 'production')
                warning(this.input !== document.activeElement, 'Input', `When Input is focused, dynamic add or remove prefix / suffix will make it lose focus caused by dom structure change. Read more: https://ant.design/components/input/#FAQ`);
        }
        return null;
    }
    componentWillUnmount() {
        if (this.removePasswordTimeout) {
            clearTimeout(this.removePasswordTimeout);
        }
    }
    focus = (option) => {
        triggerFocus(this.input, option);
    };
    blur() {
        this.input.blur();
    }
    setSelectionRange(start, end, direction) {
        this.input.setSelectionRange(start, end, direction);
    }
    select() {
        this.input.select();
    }
    saveClearableInput = (input) => {
        this.clearableInput = input;
    };
    saveInput = (input) => {
        this.input = input;
    };
    onFocus = (e) => {
        const { onFocus } = this.props;
        this.setState({ focused: true }, this.clearPasswordValueAttribute);
        onFocus?.(e);
    };
    onBlur = (e) => {
        const { onBlur } = this.props;
        this.setState({ focused: false }, this.clearPasswordValueAttribute);
        onBlur?.(e);
    };
    setValue(value, callback) {
        if (this.props.value === undefined) {
            this.setState({ value }, callback);
        }
        else {
            callback?.();
        }
    }
    handleReset = (e) => {
        this.setValue('', () => {
            this.focus();
        });
        resolveOnChange(this.input, e, this.props.onChange);
    };
    renderInput = (prefixCls, size, bordered, input = {}, breathing) => {
        const { className, addonBefore, addonAfter, size: customizeSize, disabled, htmlSize } = this.props;
        // Fix https://fb.me/react-unknown-prop
        const otherProps = omit(this.props, [
            'prefixCls',
            'onPressEnter',
            'addonBefore',
            'addonAfter',
            'prefix',
            'suffix',
            'allowClear',
            // Input elements must be either controlled or uncontrolled,
            // specify either the value prop, or the defaultValue prop, but not both.
            'defaultValue',
            'size',
            'inputType',
            'bordered',
            'htmlSize',
            'showCount'
        ]);
        const mergedClassName = classNames(getInputClassName(prefixCls, bordered, customizeSize || size, disabled, this.direction), {
            [className]: className && !addonBefore && !addonAfter
        });
        const mergedBDesignClassName = getInputClassNames(this.props, breathing, prefixCls, mergedClassName);
        return (React.createElement("input", { autoComplete: input.autoComplete, ...otherProps, onChange: this.handleChange, onFocus: this.onFocus, onBlur: this.onBlur, onKeyDown: this.handleKeyDown, className: mergedBDesignClassName, ref: this.saveInput, size: htmlSize }));
    };
    clearPasswordValueAttribute = () => {
        // https://github.com/ant-design/ant-design/issues/20541
        this.removePasswordTimeout = setTimeout(() => {
            if (this.input && this.input.getAttribute('type') === 'password' && this.input.hasAttribute('value')) {
                this.input.removeAttribute('value');
            }
        });
    };
    handleChange = (e) => {
        this.setValue(e.target.value, this.clearPasswordValueAttribute);
        resolveOnChange(this.input, e, this.props.onChange);
    };
    handleKeyDown = (e) => {
        const { onPressEnter, onKeyDown } = this.props;
        if (onPressEnter && e.keyCode === 13) {
            onPressEnter(e);
        }
        onKeyDown?.(e);
    };
    getBDesignSuffix = (suffix, props) => {
        const SuccessSuffix = React.createElement(Icon, { type: 'checkmark--outline' });
        const LoadingSuffix = (React.createElement(Loading, { className: 'anticon-loading', size: props.size === 'small' ? 'small' : 'default' }));
        let mergeSuffix = props.state === 'success' ? SuccessSuffix : suffix;
        mergeSuffix = props.state === 'loading' ? LoadingSuffix : mergeSuffix;
        return mergeSuffix;
    };
    renderShowCountSuffix = (prefixCls) => {
        const { value } = this.state;
        const { maxLength, suffix: propsSuffifx, showCount } = this.props;
        // Max length value
        const hasMaxLength = Number(maxLength) > 0;
        const suffix = this.getBDesignSuffix(propsSuffifx, this.props);
        if (suffix || showCount) {
            const valueLength = [...fixControlledValue(value)].length;
            let dataCount = null;
            if (typeof showCount === 'object') {
                dataCount = showCount.formatter({ count: valueLength, maxLength });
            }
            else {
                dataCount = `${valueLength}${hasMaxLength ? ` / ${maxLength}` : ''}`;
            }
            return (React.createElement(React.Fragment, null,
                !!showCount && (React.createElement("span", { className: classNames(`${prefixCls}-show-count-suffix`, {
                        [`${prefixCls}-show-count-has-suffix`]: !!suffix
                    }) }, dataCount)),
                suffix));
        }
        return null;
    };
    renderComponent = ({ getPrefixCls, direction, input, animation }) => {
        const { value, focused } = this.state;
        const { prefixCls: customizePrefixCls, bordered = true } = this.props;
        const prefixCls = getPrefixCls('input', customizePrefixCls);
        this.direction = direction;
        const breathing = 'breathing' in this.props ? this.props.breathing : animation?.breathing;
        const showCountSuffix = this.renderShowCountSuffix(prefixCls);
        return (React.createElement(SizeContext.Consumer, null, (size) => (React.createElement(ClearableLabeledInput, { size: size, ...this.props, prefixCls: prefixCls, inputType: 'input', value: fixControlledValue(value), element: this.renderInput(prefixCls, size, bordered, input, breathing), handleReset: this.handleReset, ref: this.saveClearableInput, direction: direction, focused: focused, triggerFocus: this.focus, bordered: bordered, suffix: showCountSuffix, breathing: breathing }))));
    };
    render() {
        return React.createElement(ConfigConsumer, null, this.renderComponent);
    }
}
export default Input;
